import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/lib/hooks/useUser';
import { useConversations, useMessages } from '@/lib/hooks/useMailbox';
import { useEmailAccounts } from '@/lib/hooks/useEmailAccount';
import { useCampaigns } from '@/lib/hooks/useCampaign';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Toggle } from '@/components/ui/toggle';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { PageHeader } from '@/components/ui/page-header';
import { MessageBubble } from '@/components/ui/message-bubble';
import DOMPurify from 'dompurify';
import { getMessageDisplay } from '@/lib/email-display';

// Skeleton component for conversation list items
function ConversationSkeleton() {
  return (
    <div className="p-5 py-5.5">
      <div className="flex items-start space-x-3">
        <Skeleton className="h-9 w-9 rounded-full" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-4 w-48 mt-1" />
          <div className="flex items-center justify-between mt-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2 w-2 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

type TabType = 'primary' | 'others';

export default function MailboxPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { emailAccounts } = useEmailAccounts();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('primary');
  const conversationsListRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { createCampaign } = useCampaigns();

  // Debounce search query with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only set debounced value if query is empty or at least 3 characters
      if (searchQuery.trim().length === 0 || searchQuery.trim().length >= 3) {
        setDebouncedSearchQuery(searchQuery.trim());
      } else {
        // Clear search if less than 3 characters
        setDebouncedSearchQuery('');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Determine isCampaign filter based on active tab
  const isCampaignFilter = activeTab === 'primary' ? true : activeTab === 'others' ? false : undefined;

  const {
    conversations,
    isLoading: conversationsLoading,
    error: conversationsError,
    markAsRead,
    deleteConversation,
  } = useConversations(user?.id?.toString() || '', debouncedSearchQuery, isCampaignFilter);

  const {
    messages,
    isLoading: messagesLoading,
    error: messagesError,
    sendReply,
  } = useMessages(user?.id?.toString() || '', selectedConversation);

  // Auto-select first conversation if none selected
  useEffect(() => {
    if (!selectedConversation && conversations.length > 0) {
      setSelectedConversation(conversations[0].id);
    }
  }, [conversations, selectedConversation]);

  // Track which conversations we've already marked as read to prevent infinite loops
  const markedAsReadRef = useRef<Set<string>>(new Set());
  
  // Clear reply box when switching conversations
  useEffect(() => {
    setReplyText('');
  }, [selectedConversation]);

  // Restore scroll position after conversations update (but not on initial load)
  useEffect(() => {
    if (conversationsListRef.current && !conversationsLoading && scrollPositionRef.current > 0) {
      // Use a small delay to ensure DOM has fully updated after revalidation
      const timeoutId = setTimeout(() => {
        if (conversationsListRef.current) {
          conversationsListRef.current.scrollTop = scrollPositionRef.current;
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [conversations, conversationsLoading]);

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversation && user?.id) {
      const conversation = conversations.find(c => c.id === selectedConversation);
      // Only mark as read if it's actually unread and we haven't already tried
      if (conversation && !conversation.is_read && !markedAsReadRef.current.has(selectedConversation)) {
        // Save scroll position before marking as read
        if (conversationsListRef.current) {
          scrollPositionRef.current = conversationsListRef.current.scrollTop;
        }
        markedAsReadRef.current.add(selectedConversation);
        markAsRead(selectedConversation).catch((err) => {
          // Silently handle errors - conversation might not exist or already be read
          console.debug('Could not mark conversation as read:', err);
          // Remove from set on error so we can retry if needed
          markedAsReadRef.current.delete(selectedConversation);
        });
      } else if (conversation && conversation.is_read) {
        // If conversation is now read, remove from tracking set
        markedAsReadRef.current.delete(selectedConversation);
      }
    }
  }, [selectedConversation, user?.id, conversations, markAsRead]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;

    const conversation = conversations.find(c => c.id === selectedConversation);
    if (!conversation) return;

    // Always provide a fallback email - backend will override for campaign conversations
    const fallbackEmail = emailAccounts.length > 0 ? emailAccounts[0].email : user?.email;
    if (!fallbackEmail) {
      console.error('No email address available for sending reply');
      return;
    }

    setIsReplying(true);
    try {
      await sendReply({
        from: fallbackEmail, // Backend will use original campaign email sender if it's a campaign conversation
        to: conversation.participants,
        subject: `Re: ${conversation.subject}`,
        body: replyText,
      });
      setReplyText('');
    } catch (error) {
      console.error('Failed to send reply:', error);
    } finally {
      setIsReplying(false);
    }
  };

  const getParticipantName = (email: string) => {
    // Check if this email belongs to the user
    const isUserEmail = emailAccounts?.some(account => account.email.toLowerCase() === email.toLowerCase());
    if (isUserEmail) {
      return 'Me';
    }
    return email.split('@')[0];
  };

  // Parse URLs in plain text and convert them to clickable links
  // Handles both direct URLs and "Text <URL>" format common in emails
  const parseLinksInText = (text: string): React.ReactNode => {
    if (!text) return text;
    
    const parts: React.ReactNode[] = [];
    let keyCounter = 0;
    let lastIndex = 0;
    
    // First, handle "Text <URL>" format (common in email footers)
    // Pattern: Text followed by <URL> where URL is http/https/www
    // Example: "Cards <https://link.moonpig.com/...>"
    const textLinkRegex = /([^<\n\r]+?)\s*<((?:https?:\/\/|www\.)[^>\s]+)>/g;
    const allMatches: Array<{ index: number; length: number; type: 'textlink' | 'url'; text?: string; url: string }> = [];
    let match: RegExpExecArray | null;
    
    // Collect all "Text <URL>" matches
    while ((match = textLinkRegex.exec(text)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        type: 'textlink',
        text: match[1].trim(),
        url: match[2].trim()
      });
    }
    
    // Also find direct URLs (not in angle brackets)
    const urlRegex = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi;
    while ((match = urlRegex.exec(text)) !== null) {
      // Check if this URL is already part of a "Text <URL>" match
      const isPartOfTextLink = allMatches.some(m => 
        m.type === 'textlink' && 
        match!.index >= m.index && 
        match!.index < m.index + m.length
      );
      
      if (!isPartOfTextLink) {
        allMatches.push({
          index: match.index,
          length: match[0].length,
          type: 'url',
          url: match[0]
        });
      }
    }
    
    // Sort all matches by index
    allMatches.sort((a, b) => a.index - b.index);
    
    // Build the result
    allMatches.forEach((linkMatch) => {
      // Add text before this link
      if (linkMatch.index > lastIndex) {
        const beforeText = text.substring(lastIndex, linkMatch.index);
        if (beforeText) {
          parts.push(beforeText);
        }
      }
      
      // Add the link
      const href = linkMatch.url.startsWith('http') ? linkMatch.url : `https://${linkMatch.url}`;
      const linkText = linkMatch.type === 'textlink' && linkMatch.text ? linkMatch.text : linkMatch.url;
      
      parts.push(
        <a
          key={`link-${keyCounter++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-link-inline hover:text-link-inline-hover underline break-all"
        >
          {linkText}
        </a>
      );
      
      lastIndex = linkMatch.index + linkMatch.length;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        parts.push(remainingText);
      }
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Sanitize HTML email content using DOMPurify for robust XSS protection
  // DOMPurify is a battle-tested library that handles edge cases better than custom solutions
  const sanitizeEmailHTML = (html: string): string => {
    if (!html) return '';

    // Configure DOMPurify with strict security settings
    const config = {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'img', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel', 'data-src'],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i, // Allow safe protocols only
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta', 'base', 'link'],
      FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick'], // Block inline styles and event handlers
      KEEP_CONTENT: true, // Keep text content even if tags are removed
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_TRUSTED_TYPE: false,
      FORCE_BODY: false,
      SANITIZE_DOM: true,
      IN_PLACE: false,
    };

    // Sanitize with DOMPurify
    let sanitized = DOMPurify.sanitize(html, config);

    // Post-process to add security attributes to links
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitized;

    // Add security attributes to all links
    const linkElements = tempDiv.querySelectorAll('a');
    linkElements.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });

    // Prevent images from loading automatically for privacy
    const imgTags = tempDiv.querySelectorAll('img');
    imgTags.forEach(img => {
      const src = img.getAttribute('src');
      if (src) {
        img.setAttribute('data-src', src);
        img.removeAttribute('src');
        // Use transparent pixel placeholder
        img.setAttribute('src', 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');
      }
    });

    return tempDiv.innerHTML;
  };

  const selectedConversationData = conversations.find(c => c.id === selectedConversation);

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="mailbox-page h-screen flex flex-row overflow-hidden" style={{ contain: 'layout paint' }}>
      <div className="flex flex-col h-full w-[45%] max-w-[640px] pl-gutter">
      {/* Header */}
      {/* No right padding: the search input sits flush with the column edge so
          the only gap to the messages panel is the standard gutter. */}
      <PageHeader title="Mailbox" className="mt-page-top mb-header-gap flex-shrink-0 !pr-0">
        <Toggle
          options={[
            { value: 'primary', label: 'Primary' },
            { value: 'others', label: 'Others' },
          ]}
          value={activeTab}
          onChange={(value) => {
            setActiveTab(value as TabType);
            setSelectedConversation(null);
          }}
          className="flex-shrink-0"
        />
        <Input
          placeholder="Search conversations"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
          className="!mb-0 max-w-sm w-70"
        />
      </PageHeader>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Left Panel - Conversations List */}
        <div className="flex flex-col h-full overflow-hidden flex-shrink-0 w-full">

          {/* Conversations List */}
          <div ref={conversationsListRef} className="flex-1 overflow-y-auto min-h-0 max-h-full w-full">
            {conversationsLoading ? (
              <>
                <ConversationSkeleton />
                <ConversationSkeleton />
                <ConversationSkeleton />
                <ConversationSkeleton />
                <ConversationSkeleton />
              </>
            ) : conversationsError ? (
              <div className="p-4 text-center text-foreground">Conversations are not available, try again later.</div>
            ) : conversations.length === 0 ? (
              <div
                className="h-full flex items-center justify-center px-4 text-center
                  pb-[var(--spacing-page-chrome)]"
              >
                {/* The list starts below the page header, so centring on the
                    list alone sits low on screen. Padding the bottom by that
                    header block pulls the message onto the viewport centre. */}
                No conversations found
              </div>
            ) : (
              conversations.map((conversation, index) => {
                const isSelected = selectedConversation === conversation.id;
                const prevConversation = index > 0 ? conversations[index - 1] : null;
                const isPrevSelected = prevConversation && selectedConversation === prevConversation.id;
                const shouldHideDivider = index > 0 && (isSelected || isPrevSelected);
                
                return (
                  <div key={conversation.id}>
                    {index > 0 && (
                      <div className={`h-px border-divider border-b mx-5 ${shouldHideDivider ? 'opacity-0' : ''}`}></div>
                    )}
                    <div
                      onClick={() => setSelectedConversation(conversation.id)}
                      className={`p-5 cursor-pointer hover:transparent transition-colors rounded-3xl bg-transparent ${
                        isSelected ? '!bg-surface-selected hover:!bg-surface-selected' : ''
                      }`}
                    >
                    <div className="flex items-start gap-3">
                      <Avatar className="h-9 w-9 outline-none flex-shrink-0">
                        <div className="h-full w-full bg-surface-avatar flex items-center justify-center text-sm font-medium rounded-full">
                          {getParticipantName(conversation.participants[0] || '').charAt(0).toUpperCase()}
                        </div>
                      </Avatar>
                      <div className="flex-1 min-w-0 max-w-full">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground truncate min-w-0">
                            {getParticipantName(conversation.participants[0] || '')}
                          </p>
                          <p className="text-xs flex-shrink-0 whitespace-nowrap">
                            {formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })}
                          </p>
                        </div>
                        <p className="text-sm text-foreground truncate min-w-0 max-w-full" title={conversation.subject}>
                          {conversation.subject}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs">{conversation.message_count} messages</p>
                          {!conversation.is_read && (
                            <div className="h-2 w-2 bg-info-solid rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>

      </div>


      </div>

      <div className="w-2/3 h-screen flex-1 pl-gutter-lg pr-gutter pt-gutter pb-gutter">

        {/* Middle Panel - Messages */}
        <div className="flex-1 flex flex-col bg-card rounded-panel overflow-hidden border border-panel-border h-full relative">
          {selectedConversation ? (
            <>
              {/* Top gradient fade */}
              <div className="h-28 bg-gradient-to-b from-card via-card/90 to-card/0 absolute top-0 left-0 right-[8px] z-[9] pointer-events-none rounded-t-4xl" />
              {/* Bottom gradient fade */}
              <div className="h-56 bg-gradient-to-b from-card/0 to-card absolute bottom-0 left-0 right-[8px] z-[8] pointer-events-none rounded-b-4xl" />

              {/* Floating Header */}
              <div className="absolute top-5 left-6 right-5 z-50 flex items-center justify-between">
                <div>
                  <h2 className="text-md font-semibold">{selectedConversationData?.subject}</h2>
                  <p className="text-sm">
                    {selectedConversationData?.participants.join(', ')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  className="shadow-xl bg-card"
                  onClick={() => {
                    if (selectedConversation) {
                      deleteConversation(selectedConversation);
                      setSelectedConversation(null);
                    }
                  }}
                >
                  Delete
                </Button>
              </div>

              {/* Messages */}
              <div className="h-full overflow-y-auto px-9 pt-24 pb-52 space-y-4 relative z-[1] [&::-webkit-scrollbar-track]:mt-6 [&::-webkit-scrollbar-track]:mb-6">
                {messagesLoading ? (
                  <div className="text-center">Loading messages...</div>
                ) : messagesError ? (
                  <div className="text-center text-destructive">Failed to load messages</div>
                ) : messages.length === 0 ? (
                  <div className="text-center">No messages in this conversation</div>
                ) : (
                  messages.map((message) => {
                    // Show the message as written: HTML rendered (not its source),
                    // and the quoted history a reply carries stripped out.
                    const display = getMessageDisplay(message.body, message.html_body);

                    return (
                      <MessageBubble
                        key={message.id}
                        isOutgoing={message.is_outgoing}
                      >
                        {display.kind === 'text' ? (
                          <p className="text-sm whitespace-pre-wrap break-words">{parseLinksInText(display.text)}</p>
                        ) : display.kind === 'html' ? (
                          <div
                            className="text-sm break-words email-content [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                            dangerouslySetInnerHTML={{ __html: sanitizeEmailHTML(display.html) }}
                          />
                        ) : (
                          <p className="text-sm text-content-subtle italic">No message content sent.</p>
                        )}
                        <p className="text-xs mt-3">
                          {formatDistanceToNow(new Date(message.sent_at), { addSuffix: true })}
                        </p>
                      </MessageBubble>
                    );
                  })
                )}
              </div>

              {/* Floating Reply Box */}
              <div className="absolute bottom-8 left-6 right-6 z-50">
                {activeTab === 'primary' && selectedConversationData?.is_campaign === true && !selectedConversationData?.is_non_replyable ? (
                  <div className="relative">
                    <Textarea
                      placeholder="Type your reply..."
                      value={replyText}
                      rows={3}
                      onChange={(e) => {
                        setReplyText(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(300, Math.max(120, e.target.scrollHeight)) + 'px';
                      }}
                      className="flex-1 !min-h-[120px] resize-none bg-card shadow-md !pb-18 overflow-y-auto !pt-4"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleSendReply();
                        }
                      }}
                    />
                    {replyText.trim() && (
                      <Button
                        onClick={handleSendReply}
                        disabled={isReplying}
                        variant="primary"
                        className="absolute bottom-1.5 left-1.5"
                      >
                        Send
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[120px] bg-card rounded-lg border border-border shadow-xl">
                    <p className="text-sm text-center px-4">
                      {selectedConversationData?.is_non_replyable
                        ? "You cannot reply to this conversation"
                        : "Replies are only available for campaign conversations in the Primary tab"}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : conversations.length === 0 && !conversationsLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <h3 className="text-lg font-medium text-foreground mb-2">You don't have any conversations</h3>
                <p className="text-sm mb-6">Start campaigns to generate replies</p>
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="primary">
                      Create a Campaign
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader className="ml-2 mt-2 mb-4">
                      <DialogTitle>Create New Campaign</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!newCampaignName.trim()) return;

                      setIsCreating(true);
                      try {
                        const newCampaign = await createCampaign({ name: newCampaignName.trim() });
                        setNewCampaignName('');
                        setIsCreateDialogOpen(false);
                        
                        // Navigate to the campaign editor
                        navigate(`/dashboard/campaigns/${newCampaign.id}/edit`);
                      } catch (error) {
                        console.error('Error creating campaign:', error);
                      } finally {
                        setIsCreating(false);
                      }
                    }} className="space-y-4">
                      <div>
                        <Input
                          id="campaign-name"
                          value={newCampaignName}
                          onChange={(e) => setNewCampaignName(e.target.value)}
                          placeholder="Campaign title"
                          inlineLabel="Name"
                          className="w-full mt-2"
                          autoFocus
                          required
                        />
                      </div>
                      <div className="flex gap-3 pt-6 justify-end -mr-2 -mb-2">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setIsCreateDialogOpen(false)}
                          disabled={isCreating}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={isCreating || !newCampaignName.trim()}
                          variant="primary"
                        >
                          {isCreating ? 'Creating...' : 'Create Campaign'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h3 className="text-lg font-medium">No conversation selected</h3>
                <p className="text-sm">Choose a conversation from the sidebar to get started</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
}
